// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Walkthrough from "../Walkthrough/Walkthrough";
import {
  DataAgentsCard,
  MockButton,
  MockDataHubTabs,
  MockDialog,
  MockField,
  MockHubCard,
  MockNavbar,
  MockPage,
  MockSnack,
} from "../GuideMocks/PortalMocks";
import {
  DownloadsFolder,
  ExtractedFolder,
  InstallerReady,
  InstallerWelcome,
} from "../GuideMocks/WindowsMocks";
import { GUIDE_NAMESPACE } from "../GuideText/GuideText";

// Utils
import { WalkthroughStep } from "../Walkthrough/walkthroughState";

const FIND_STEPS: Array<WalkthroughStep> = [
  {
    screen: "nav",
    target: "nav-dh",
    text: "At the top of the Clinic Portal, click <b>Data Hub</b>.",
  },
  {
    screen: "dh",
    target: "tab-config",
    text: "You land on <b>Data Hub Config</b>. Your hubs are listed here.",
  },
  {
    target: "tab-processed",
    text: "<b>Processed Exams</b> lists every exam the hub brought in. You come here to check patients.",
  },
];

export const FindWalkthrough = () => {
  const { t } = useTranslation();
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Walkthrough id="find" steps={FIND_STEPS}>
      {(view) => (
        <Box>
          <MockNavbar
            current={view.screen === "nav" ? "Dashboard" : "Data Hub"}
            dataHubTarget="nav-dh"
          />
          <MockPage attached>
            {view.screen === "nav" ? (
              <Box padding="16px">
                <Typography fontSize="16px" fontWeight={600}>
                  {tg("Your dashboard")}
                </Typography>
              </Box>
            ) : (
              <>
                <MockDataHubTabs
                  current={0}
                  configTarget="tab-config"
                  processedTarget="tab-processed"
                />
                <DataAgentsCard>
                  <Typography fontSize="14px">
                    {t("No EMR hubs found.")}
                  </Typography>
                </DataAgentsCard>
              </>
            )}
          </MockPage>
        </Box>
      )}
    </Walkthrough>
  );
};

const ADD_HUB_STEPS: Array<WalkthroughStep> = [
  {
    screen: "empty",
    target: "add",
    text: "In the <b>Data Agents</b> box, click <b>Add</b>.",
  },
  {
    screen: "dlg",
    target: "alias",
    text: "The <b>Add EMR Hub</b> window opens. Click in the <b>Alias</b> box.",
  },
  {
    on: ["alias"],
    target: "alias",
    text: "Type a name that says where the PC is, for example <b>Front desk PC</b>.",
  },
  { target: "dlg-add", text: "Click <b>Add</b>." },
  {
    screen: "done",
    target: "pending",
    text: "The new hub shows <b>Pending</b>. That is normal: it is not installed on a PC yet.",
  },
  {
    target: "snack",
    text: "A green message confirms it. Next, install the hub on the PC.",
  },
];

export const AddHubWalkthrough = () => {
  const { t } = useTranslation();
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Walkthrough id="add-hub" steps={ADD_HUB_STEPS}>
      {(view) => (
        <>
          {view.screen === "empty" && (
            <MockPage>
              <MockDataHubTabs current={0} />
              <DataAgentsCard addTarget="add">
                <Typography fontSize="14px">
                  {t("No EMR hubs found.")}
                </Typography>
              </DataAgentsCard>
            </MockPage>
          )}
          {view.screen === "dlg" && (
            <MockDialog
              title={t("Add EMR Hub")}
              maxWidth="420px"
              footer={
                <>
                  <MockButton kind="cancel">{t("Cancel")}</MockButton>
                  <MockButton
                    kind="confirm"
                    disabled={!view.isOn("alias")}
                    targetId="dlg-add"
                  >
                    {t("Add")}
                  </MockButton>
                </>
              }
            >
              <MockField
                label={t("Alias")}
                required
                placeholder={t("Enter a value")}
                value={tg("Front desk PC")}
                filled={view.isOn("alias")}
                targetId="alias"
              />
            </MockDialog>
          )}
          {view.screen === "done" && (
            <MockPage>
              <MockDataHubTabs current={0} />
              <DataAgentsCard>
                <MockHubCard
                  status="Pending"
                  clinics={tg("Maple Eye Clinic")}
                  network=""
                  targets={{ status: "pending" }}
                />
              </DataAgentsCard>
              <MockSnack
                text={t("EMR hub created successfully!")}
                targetId="snack"
              />
            </MockPage>
          )}
        </>
      )}
    </Walkthrough>
  );
};

const INSTALL_STEPS: Array<WalkthroughStep> = [
  {
    screen: "card",
    target: "dl",
    text: "On the hub PC, open the portal and click the <b>download</b> icon on the hub card.",
  },
  {
    screen: "files",
    target: "zip",
    text: "A zip file named after your hub lands in <b>Downloads</b>. Right-click it.",
  },
  {
    show: ["ctx"],
    target: "extract",
    text: "Choose <b>Extract All...</b>, then <b>Extract</b>.",
  },
  {
    screen: "extracted",
    target: "json",
    text: "You get two files. Keep them together in the same folder: <b>enrollment.json</b> is the hub's one-time key.",
  },
  { target: "msi", text: "Double-click <b>EmrHub.msi</b> to start setup." },
  {
    screen: "welcome",
    target: "w-next",
    text: "Setup opens. Click <b>Next</b>.",
  },
  {
    screen: "ready",
    target: "r-msg",
    text: "Setup checks with RetinaLogik and shows the hub's name and your clinic. Make sure they are right.",
  },
  {
    target: "r-install",
    text: "Click <b>Install</b>. When Windows asks for permission, click <b>Yes</b>.",
  },
  {
    screen: "finish",
    target: "f-msg",
    text: "Done. The hub now runs in the background, and starts again by itself after a restart.",
  },
  {
    target: "f-btn",
    text: "Click <b>Finish</b>. You can close the folder too.",
  },
  {
    screen: "active",
    target: "active",
    text: "Back in the portal, within a minute or two the card turns <b>Active</b>.",
  },
  {
    target: "net",
    text: "It also shows the PC's network address. Machine technicians may need it later.",
  },
];

export const InstallWalkthrough = () => {
  const { t } = useTranslation();
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Walkthrough id="install" steps={INSTALL_STEPS}>
      {(view) => (
        <>
          {view.screen === "card" && (
            <MockHubCard
              status="Pending"
              clinics={tg("Maple Eye Clinic")}
              tooltip={{ on: "download", text: t("Download EMR Installer") }}
              targets={{ download: "dl" }}
            />
          )}
          {view.screen === "files" && (
            <DownloadsFolder
              showMenu={view.shows("ctx")}
              zipTarget="zip"
              extractTarget="extract"
            />
          )}
          {view.screen === "extracted" && (
            <ExtractedFolder msiTarget="msi" jsonTarget="json" />
          )}
          {view.screen === "welcome" && (
            <InstallerWelcome
              heading="Welcome to the RetinaLogik EMR Hub Setup Wizard"
              lines={[
                "The Setup Wizard will install RetinaLogik EMR Hub on your computer. Click Next to continue or Cancel to exit the Setup Wizard.",
              ]}
              nextTarget="w-next"
            />
          )}
          {view.screen === "ready" && (
            <InstallerReady messageTarget="r-msg" installTarget="r-install" />
          )}
          {view.screen === "finish" && (
            <InstallerWelcome
              heading="Completed the RetinaLogik EMR Hub Setup Wizard"
              lines={[
                "Click the Finish button to exit the Setup Wizard.",
                "This PC is enrolled as Front desk PC for Maple Eye Clinic and connected to RetinaLogik. The RetinaLogik EMR Hub service is running.",
              ]}
              lineTarget="f-msg"
              nextLabel="Finish"
              nextTarget="f-btn"
            />
          )}
          {view.screen === "active" && (
            <MockHubCard
              status="Active"
              clinics={tg("Maple Eye Clinic")}
              network="3C:52:82:1A:7F:04 : 10.0.0.15"
              version="2.5.0"
              targets={{ status: "active", network: "net" }}
            />
          )}
        </>
      )}
    </Walkthrough>
  );
};

const ANATOMY_STEPS: Array<WalkthroughStep> = [
  {
    target: "status",
    text: "<b>Status.</b> Green means all good. Orange means it needs attention, and says how many problems.",
  },
  { target: "alias", text: "<b>The hub's name</b>, the one you chose." },
  {
    target: "clinics",
    text: "<b>Associated Clinics</b>: which of your clinics this hub works for.",
  },
  {
    target: "net",
    text: "<b>Network Interfaces</b>: the hub PC's network address (the numbers after the colon). Technicians use it to point machines at the hub.",
  },
  {
    target: "needs",
    text: "<b>Needs Attention</b>: files the hub tried to send many times and gave up on. Above 0, contact RetinaLogik support.",
  },
  {
    target: "queue",
    text: "<b>Queue</b>: files waiting to be sent. A small number that goes down is normal.",
  },
  {
    target: "problems",
    text: "<b>Problems</b>: red when above 0. Click it to see what is wrong.",
  },
  {
    target: "version",
    text: "<b>App Version</b>. An orange label means a newer hub is available.",
  },
  {
    target: "conn",
    text: "<b>Connections</b>: set up what the hub connects to. This is the next part of the manual.",
  },
  { target: "jobs", text: "<b>Jobs</b>: the history of what the hub sent." },
  {
    target: "dl",
    text: "<b>Download</b> the installer, or the update when it is orange.",
  },
  { target: "edit", text: "<b>Edit</b> the hub's name and clinics." },
  { target: "del", text: "<b>Delete</b> the hub. This cannot be undone." },
];

export const HubCardWalkthrough = () => {
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);

  return (
    <Walkthrough id="hub-card" steps={ANATOMY_STEPS}>
      {() => (
        <MockHubCard
          status="Active"
          problems={1}
          clinics={tg("Maple Eye Clinic, Maple Eye Clinic North")}
          network="3C:52:82:1A:7F:04 : 10.0.0.15"
          needs={2}
          queue={5}
          version="2.4.0"
          update="2.5.0"
          targets={{
            status: "status",
            alias: "alias",
            clinics: "clinics",
            network: "net",
            needs: "needs",
            queue: "queue",
            problems: "problems",
            version: "version",
            connections: "conn",
            jobs: "jobs",
            download: "dl",
            edit: "edit",
            delete: "del",
          }}
        />
      )}
    </Walkthrough>
  );
};
